import React, { useState, useEffect } from "react";
import { Box, Button, Tabs, Tab, Typography, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useParams } from "react-router-dom";
import EditProductModal from "./EditProductModal";
import DeleteProductModal from "./DeleteProductModal";
import { ProductTabsConfig } from "./constants/productTabsConfig";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import {
  useGetProductDetail,
  useHasActiveProductItems
} from "../../hooks/api/useProduct";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import NoProductDetailFound from "./NoProductDetailFound";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import ProductInventorySummary from "./ProductInventorySummary";
import { ProductInventoryFilterKey } from "./types/products";
import InventoryPageHeader from "../common/PageHeader/InventoryPageHeader";
import { formatSellingMode } from "./utils/productUtils";

const PRODUCT_ITEMS_TAB_INDEX = 1;

const ProductView: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const product = useSelector(
    (state: RootState) => state.products.productDetail
  );
  const { t } = useKivunovaTranslation();
  const getProductDetailMutation = useGetProductDetail();
  const { hasActive, loading: checkingItems } =
    useHasActiveProductItems(productId);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [tab, setTab] = useState(0);
  const [itemStatusFilter, setItemStatusFilter] = useState("");

  const handleInventoryStatClick = (filterKey: ProductInventoryFilterKey) => {
    setItemStatusFilter(filterKey === "total" ? "" : filterKey);
    setTab(PRODUCT_ITEMS_TAB_INDEX);
  };

  useEffect(() => {
    (async () => {
      try {
        if (productId) {
          await getProductDetailMutation.mutateAsync({ id: productId });
        }
      } catch (error) {
        console.error("Failed to fetch product details", error);
      }
    })();
  }, [productId]);

  if (getProductDetailMutation.isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-products", "Loading products...")}
      />
    );
  }

  if (!product) {
    return <NoProductDetailFound />;
  }

  const tabsConfig = ProductTabsConfig(product, {
    itemListProps: {
      statusFilter: itemStatusFilter,
      onStatusFilterChange: setItemStatusFilter
    }
  });

  return (
    <Box sx={{ color: "text.primary" }}>
      <InventoryPageHeader
        title={product.name}
        subtitle={`${t("product-detail-selling-mode", "Selling mode")}: ${formatSellingMode(product.sellingMode, t)}`}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setOpenEdit(true)}
              sx={{
                textTransform: "none",
                borderColor: BRAND_COLOR,
                color: BRAND_COLOR,
                fontWeight: 600,
                fontSize: "0.8125rem",
                "&:hover": {
                  borderColor: BRAND_HOVER,
                  backgroundColor: BRAND_HOVER,
                  color: "white"
                }
              }}
            >
              {t("edit", "Edit")}
            </Button>
            <Box data-testid="product-detail-delete-slot">
              {checkingItems ? (
                <Box sx={{ minWidth: 88, height: 36.5 }} aria-hidden />
              ) : (
                <Tooltip
                  title={
                    hasActive
                      ? t(
                          "delete-product.has-active-items-tooltip",
                          "This product has active inventory items. Sell or remove all items before deleting."
                        )
                      : ""
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutlineOutlinedIcon />}
                      onClick={() => setOpenDelete(true)}
                      disabled={hasActive}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8125rem"
                      }}
                    >
                      {t("delete", "Delete")}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </>
        }
      />

      <ProductInventorySummary
        product={product}
        activeFilter={itemStatusFilter}
        onStatCardClick={handleInventoryStatClick}
      />

      {/* Tabs Header */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          minHeight: 40,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            minHeight: 40
          },
          "& .MuiTabs-indicator": {
            backgroundColor: BRAND_COLOR,
            height: 3,
            borderRadius: 1.5
          }
        }}
        aria-label={t("product-view-tabs-aria", "Product tabs")}
      >
        {tabsConfig.map(({ id, labelKey, defaultLabel, ariaControls }) => (
          <Tab
            key={id}
            data-testid={id === 0 ? "product-tab-details" : "product-tab-items"}
            label={
              <Typography variant="body1">
                {t(labelKey, defaultLabel)}
              </Typography>
            }
            id={`product-tab-${id}`}
            aria-controls={ariaControls}
            sx={{
              textTransform: "none",
              color: "black",
              "&.Mui-selected": { color: BRAND_COLOR },
              "&:hover": { color: BRAND_HOVER }
            }}
          />
        ))}
      </Tabs>

      {/* Tab Panels */}
      {tabsConfig.map(
        ({ id, content }) =>
          tab === id && (
            <Box
              key={id}
              id={`product-tabpanel-${id}`}
              role="tabpanel"
              aria-labelledby={`product-tab-${id}`}
              sx={{ mt: 2 }}
            >
              {content}
            </Box>
          )
      )}

      {/* Modals */}
      <EditProductModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        product={product}
      />
      {product && (
        <DeleteProductModal
          open={openDelete}
          onClose={() => setOpenDelete(false)}
          product={product}
        />
      )}
    </Box>
  );
};

export default ProductView;
